import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import BaseModal from 'components/Modal';
import {
  emailSelector,
  selectionSelector,
  showSelector,
  isLoadingStateSelector,
  isExportStateSelector,
  exportResultSelector,
  typeOfExportSelector,
} from 'containers/PageHeader/ExportModal/selectors';
import {
  changeEmail,
  setSelection,
  setShow,
  setIsLoadingState,
  setIsExportState,
  setExportResult,
  setExportType,
  resetExportType,
} from 'containers/PageHeader/ExportModal/actions';
import { Row, Col } from 'react-bootstrap';
import './styles.scss';
import Input from 'components/Inputs/Input';
import ModalButton from 'components/Modal/Button';
import { EXPORT_PDF_XLS } from 'apollo/queries';
import { isValidEmail } from 'utils/functions';
import { clientIdSelector } from 'store/global/selectors';
import { GlobalFilterType, SELECTION_TO_TYPE } from 'utils/types';
import {
  PROCESSING_EXPORTS,
  ERROR_PROCESSING_EXPORTS,
  CURRENT_PAGE_EXPORT,
  RAW_WEEKLY_EXPORT,
  RAW_WEEKLY_TEMPLATE,
  CONFIRM_YOUR_EMAIL,
} from 'utils/strings';
import { ApolloConsumer } from '@apollo/client/react/context';
import LoadingSpinner from 'components/LoadingSpinner';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FileTypes from '../../../components/PageHeader/Export/FileTypes';
import { ApolloClient } from '@apollo/client';
import ExportCards from 'components/PageHeader/Export/ExportCards';

interface Props {
  clientId: string;
  show: boolean;
  email: string;
  template: string;
  pdfAvailable?: boolean;
  isLoadingState: boolean;
  isExportState: boolean;
  exportResult: boolean;
  globalFilter: GlobalFilterType;
  selection: Record<string, boolean>;
  typeOfExport: Record<string, boolean>;
  onClose: () => void;
  onEmailChange: (email: string) => void;
  onSelection: (key: string) => void;
  onTypeOfExport: (key: string) => void;
  onResponse: (isExportState: boolean, exportResult: boolean) => void;
  onLoading: () => void;
}

const ExportModal: React.FC<Props> = ({
  show,
  email,
  clientId,
  template,
  pdfAvailable,
  globalFilter,
  selection,
  typeOfExport,
  isLoadingState,
  isExportState,
  exportResult,
  onClose,
  onEmailChange,
  onSelection,
  onTypeOfExport,
  onLoading,
  onResponse,
}) => {
  useEffect(() => {
    if (show && isExportState) {
      setTimeout(() => {
        onClose();
      }, 5000);
    }
  });

  const executeExportDataQuery = async (
    client: ApolloClient<any>,
    template: string,
    exportType: string
  ): Promise<boolean> => {
    const { data } = await client.query({
      query: EXPORT_PDF_XLS,
      variables: {
        template: template,
        client: clientId,
        exportType: exportType,
        recipients: [email],
        globalFilter: globalFilter,
      },
    });
    return data.export !== undefined && data.export !== null ? data.export : false;
  };

  const exportData = async (client) => {
    onLoading();
    let result = false;

    if (typeOfExport[RAW_WEEKLY_EXPORT]) {
      result = await executeExportDataQuery(client, RAW_WEEKLY_TEMPLATE, 'XLSX');
    } else {
      const promises = Object.keys(selection).map(async (key) => {
        if (selection[key]) {
          return executeExportDataQuery(client, template, SELECTION_TO_TYPE.get(key));
        } else {
          return new Promise((resolve) => resolve(true));
        }
      });
      result = (await Promise.all(promises)).every((v) => v === true);
    }
    onResponse(true, result);
  };

  const isExportButtonDisabled =
    (typeOfExport[CURRENT_PAGE_EXPORT]
      ? Object.keys(selection).filter((key) => selection[key]).length === 0
      : !typeOfExport[RAW_WEEKLY_EXPORT]) || !isValidEmail(email);

  const PocessingExport = exportResult ? (
    <div className="result-container">
      <div className="result-icon-success">
        <FontAwesomeIcon icon={faCheckCircle} />
      </div>
      <div className="result-text">{PROCESSING_EXPORTS}</div>
    </div>
  ) : (
    <div className="result-container">
      <div className="result-icon-error">
        <FontAwesomeIcon icon={faTimesCircle} />
      </div>
      <div className="result-text">{ERROR_PROCESSING_EXPORTS}</div>
    </div>
  );

  const ConfirmEmail = (
    <Row className="email-container mt-5">
      <Col>
        <div className="title">Email Address</div>
        <p>{CONFIRM_YOUR_EMAIL}</p>
        <Input
          placeholder="Email"
          isValid={isValidEmail(email)}
          value={email}
          onChange={onEmailChange}
        />
      </Col>
    </Row>
  );

  return (
    <BaseModal
      baseClassName="export-modal"
      buttons={
        !isExportState &&
        !isLoadingState && [
          <ModalButton primary={false} onClick={onClose}>
            Cancel
          </ModalButton>,
          <ApolloConsumer>
            {(client) => (
              <div>
                <ModalButton disabled={isExportButtonDisabled} onClick={() => exportData(client)}>
                  Export All
                </ModalButton>
              </div>
            )}
          </ApolloConsumer>,
        ]
      }
      show={show}
      size="lg"
      title="Export Modal"
      onClose={onClose}
    >
      {isExportState ? (
        PocessingExport
      ) : isLoadingState ? (
        <div className="spinner-modal-body">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="export-modal-body">
          <ExportCards
            pdfAvailable={pdfAvailable}
            exportType={typeOfExport}
            onSelection={onSelection}
            onExportTypeChecked={onTypeOfExport}
          />
          {typeOfExport[CURRENT_PAGE_EXPORT] && pdfAvailable ? (
            <FileTypes onSelection={onSelection} />
          ) : null}
          {ConfirmEmail}
        </div>
      )}
    </BaseModal>
  );
};

const mapStateToProps = (state) => ({
  clientId: clientIdSelector(state),
  email: emailSelector(state),
  selection: selectionSelector(state),
  typeOfExport: typeOfExportSelector(state),
  show: showSelector(state),
  isLoadingState: isLoadingStateSelector(state),
  isExportState: isExportStateSelector(state),
  exportResult: exportResultSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  onClose: () => {
    dispatch(setShow(false));
    dispatch(setIsLoadingState(false));
    dispatch(setIsExportState(false));
    dispatch(setExportResult(false));
    dispatch(resetExportType());
  },
  onEmailChange: (email: string) => {
    dispatch(changeEmail(email));
  },
  onSelection: (key: string) => {
    dispatch(setSelection(key));
  },
  onTypeOfExport: (key: string) => {
    dispatch(setExportType(key));
  },
  onLoading: () => {
    dispatch(setIsLoadingState(true));
  },
  onResponse: (isExportState: boolean, exportResult: boolean) => {
    dispatch(setIsLoadingState(false));
    dispatch(setIsExportState(isExportState));
    dispatch(setExportResult(exportResult));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ExportModal);
